import { useBundleDownload } from '@/hooks/useBundleDownload';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CardHeader } from '@/components/ui/card';
import { CardTitle } from '@/components/ui/card';
import { CardContent } from '@/components/ui/card';
import { CardDescription } from '@/components/ui/card';
import { Download, Trash2 } from 'lucide-react';
import type { AssetBundle, ProductAsset } from '@/services/productService';
import React from 'react';
import { config } from '@/config/config';
import { formatDateForDisplay, DateFormat } from '@/utils/numberFormat';
import { useOrgSettings } from '@/hooks/useOrgSettings';
import { PermissionGuard } from '@/components/common/PermissionGuard';

interface BundleCardProps {
  bundle: AssetBundle;
  productId: number;
  assets: ProductAsset[];
  onDelete: (bundleId: number) => void;
  isImageAsset: (asset: ProductAsset) => boolean;
  getAssetIcon: (type: string) => JSX.Element;
}

export function BundleCard({
  bundle,
  productId,
  assets,
  onDelete,
  isImageAsset,
  getAssetIcon,
}: BundleCardProps) {
  const { download: downloadBundle } = useBundleDownload(productId, bundle.id);
  const bundleAssets = assets.filter(asset => bundle.asset_ids.includes(asset.id));
  const bundleConfig = config.productDetailTabs.assets.bundleCard;

  // Get organization settings for date formatting
  const { orgSettings } = useOrgSettings();
  const dateFormat = (orgSettings?.date_format || 'MDY') as DateFormat;

  return (
    <Card key={bundle.id} className="min-w-0 overflow-hidden rounded-lg shadow">
      <CardHeader className="p-6 pb-3">
        <div className="flex items-start justify-between">
          <CardTitle className="text-lg font-medium">{bundle.name}</CardTitle>
          <div className="flex gap-1">
            <PermissionGuard permission="product.change">
              <Button
                variant="ghost"
                size="icon"
                onClick={downloadBundle}
                title={bundleConfig.download_title}
              >
                <Download className="size-4" />
              </Button>
            </PermissionGuard>
            <PermissionGuard permission="product.delete">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onDelete(bundle.id)}
                title={bundleConfig.delete_title}
              >
                <Trash2 className="size-4 text-red-500" />
              </Button>
            </PermissionGuard>
          </div>
        </div>
        <CardDescription className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">
            {formatDateForDisplay(bundle.created_at, dateFormat)}
          </span>
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
            {bundleConfig.assets_count.replace('{{count}}', bundle.asset_ids.length.toString())}
          </span>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2 p-6 pt-2">
        <div className="flex flex-wrap gap-1">
          {bundleAssets.slice(0, 5).map(asset => (
            <div
              key={asset.id}
              className="flex size-10 items-center justify-center overflow-hidden rounded bg-muted"
              title={asset.name}
            >
              {isImageAsset(asset) ? (
                <img src={asset.url} alt={asset.name} className="size-full object-cover" />
              ) : (
                getAssetIcon(asset.type)
              )}
            </div>
          ))}
          {bundle.asset_ids.length > 5 && (
            <div className="flex size-10 items-center justify-center rounded bg-muted text-xs font-medium">
              +{bundle.asset_ids.length - 5}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
